import { Component, OnInit, Input, Output, SimpleChanges, EventEmitter, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MessageService } from '../../../../services/message.service';
import { MasterService } from '../../../../services/master.service';
import { StoreService } from '../../../../services/store.service';
import * as words from '../../../../services/language';
import { formatPhone, formatCurrency } from 'src/app/utils/format';
import { BANK_DRAFT_RETURN_CODES } from 'src/app/constants/payments';
import { clean } from 'src/app/utils/numeric';
import { ReturnCode } from 'src/app/models/return-code.model';
import { Receipt } from 'src/app/models/receipt.model';
import { Subscription } from 'rxjs';
import { Form } from 'src/app/models/form.model';

@Component({
    selector: 'receipt',
    templateUrl: './receipt.component.html',
    styleUrls: ['./receipt.component.scss'],
    standalone: false
})
export class ReceiptComponent implements OnInit, OnDestroy {

    // Unique internal identity of the receipt.
    @Input() id: number = 0

    /** Unique key so that  multiple components can be on the same page. */
    @Input() key: string | null = null

    // Currently selected language
    @Input() language: string = 'EN'

    // Word replacements for each language.
    @Input() words: Object = words.language

    // Communicate when the user has finished viewing the receipt.
    @Output() closed: EventEmitter<Boolean> = new EventEmitter()

    /** Indicates the page is fetching the receipt details. */
    private loading: boolean = false

    /** The fetched/loaded receipt details. */
    protected receipt: Receipt | null = null

    /** Form used to process a refund on the receipt. */
    protected form: FormGroup | null = null

    /** Form used to process a void on the receipt. */
    protected voidForm: FormGroup | null = null

    /** Form used to process a return on the receipt. */
    protected returnForm: FormGroup | null = null

    private subscriptions: Subscription[] = []

    /** Indicates the user is currently working to refund the receipt. */
    protected refunding: boolean = false

    /** Indicates the user is currently working to void the receipt. */
    protected voiding: boolean = false

    /** Indicates the user is currently working to return the receipt. */
    protected returning: boolean = false

    /** A list of available ACH (bank draft) return codes for use when manually marking a returned receipt. */
    protected codes: ReturnCode[] = BANK_DRAFT_RETURN_CODES

    /** The currently selected lot. */
    private lotId: number = 0

    /** The queried form identity for a receipt. */
    protected formId: number = 0

    /** */
    protected pdf: any = null

    /**
     * Are all payments associated with this receipt (and 
     * therefore the receipt itself) discarded?
     */
    get discarded(): boolean {
        if (!this.receipt || !this.receipt.id) {
            return false
        }

        return (this.receipt.status.toLocaleLowerCase() === 'discarded')
    }

    /**
     * Is the payment method a credit or debit card?
     */
    get card(): boolean {
        if (!this.receipt || !this.receipt.id) {
            return false
        }

        if (!this.receipt.meta || (this.receipt.meta.length <= 0)) {
            return false
        }

        return (['credit card', 'debit card', 'pre-paid debit card'].includes(this.receipt.meta[0].method))
    }

    /**
     * Is the payment method cash?
     */
    get cash(): boolean {
        if (!this.receipt || !this.receipt.id) {
            return false
        }

        if (!this.receipt.meta || (this.receipt.meta.length <= 0)) {
            return false
        }

        return (this.receipt.meta[0].method.toLowerCase() === 'cash')
    }

    /**
     * Is the payment method a bank draft?
     */
    get draft(): boolean {
        if (!this.receipt || !this.receipt.id) {
            return false
        }

        if (!this.receipt.meta || (this.receipt.meta.length <= 0)) {
            return false
        }

        return (['bank draft'].includes(this.receipt.meta[0].method))
    }

    /**
     * Is the payment method a check?
     */
    get check(): boolean {
        if (!this.receipt || !this.receipt.id) {
            return false
        }

        if (!this.receipt.meta || (this.receipt.meta.length <= 0)) {
            return false
        }

        return (['check', 'travelers check', 'cashiers check', 'money order'].includes(this.receipt.meta[0].method))
    }

    /**
     * Is the payment method an ach payment?
     */
    get ach(): boolean {
        if (!this.receipt || !this.receipt.id) {
            return false
        }

        if (!this.receipt.meta || (this.receipt.meta.length <= 0)) {
            return false
        }

        return (['ach checking', 'ach savings'].includes(this.receipt.meta[0].method))
    }

    /**
     * Is there any amount left to edit (refund, void or return)?
     */
    get editable(): boolean {
        if (!this.receipt || !this.receipt.id) {
            return false
        }

        return (this.receipt.amount - this.receipt.refund) > 0
    }

    constructor (private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService, private fb: FormBuilder, private sanitizer: DomSanitizer) {
        this.form = fb.group({
            refund: new FormControl<number>(0, [Validators.required]),
            reason: new FormControl<string>('', [Validators.required])
        })

        this.voidForm = fb.group({
            voided: new FormControl<Date | null>(null, [Validators.required]),
            reason: new FormControl<string>('', [Validators.required])
        })

        this.returnForm = fb.group({
            returned: new FormControl<Date | null>(null, [Validators.required]),
            code: new FormControl<string>('', []),
            reason: new FormControl<string>('', [Validators.required]),
            fee: new FormControl<string>('0.00', [])
        })
    }

    ngOnInit() {
        this.lotId = parseInt(localStorage.getItem('lot') || this.store.lotSelected)
        this.fetchForm()

        this.subscriptions.push(
            this.returnForm.get('code').valueChanges.subscribe((code) => {
                const found = this.codes.find((item) => item.code === code)
                if (!found) {
                    return
                }

                this.returnForm.get('reason').setValue(found.reason, {emitEvent: false})
            })
        )
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }
        
        if (changes.id) {
            if (!this.id || (this.id <= 0)) {
                return
            }

            this.fetchReceipt()
            this.fetchForm()
        }
    }

    /**
     * After closing the modal lets clean things up a bit.
     */
    private reset = (): void => {
        this.receipt = null
        this.refunding = false
        this.voiding = false
        this.returning = false
        this.form.reset()
        this.form.get('refund').setValue(formatCurrency(0))
        this.form.get('reason').setValue('')
        this.voidForm.reset()
        this.returnForm.reset()
    }

    private fetchReceipt = () => {
        if (!this.id || (this.id <= 0)) {
            return
        }

        this.master.get(`receipts/${this.id}`, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.receipt = res.data
        })
    }

    /**
     * Determine the form identity for a receipt based on the alias.
     */
    private fetchForm = () => {
        if (this.formId && (this.formId > 0)) {
            this.fetch()
        }

        if (this.loading) {
            return
        }

        this.loading = true
        this.master.get(`forms?panel=none&module=receipts&category=other`, (res) => {
            this.loading = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.forms || (res.data.forms.length <= 0)) {
                return
            }

            for (const form of (res.data.forms as Array<Form>)) {
                if (!form.alias || (form.alias !== 'receipt')) {
                    continue
                }

                this.formId = form.id
            }
            
            this.fetch()
        })
    }

    /**
    * Construct and download the receipt pdf via the API.
    */
    public fetch = () => {
        if (!this.id || this.id <= 0 || !this.formId || (this.formId <= 0)) {
            return
        }

        if (this.loading) {
            return
        }

        this.pdf = null
        const payload = {
            forms: [
                {
                    formId: this.formId,
                    module: 'receipts',
                    copies: 1,
                    xChange: null,
                    yChange: null
                }
            ]
        }

        const btn = document.getElementById(`toggleReceiptView-${this.key || ''}`) as HTMLButtonElement
        if (btn) {
            btn.click()
        }

        this.loading = true
        this.master.post(`forms?lotId=${this.lotId}&receiptId=${this.id}`, payload, (meta) => {
            this.loading = false

            if (!meta || !meta.status || !meta.data || !meta.data.url) {
                this.ms.sendMessage("alert", { type: "danger", text: (meta && meta.error) ? meta.error : this.words[this.language]['apiNoResponse'] })
                return
            }

            const url: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(meta.data.url)
            if (!url) {
                this.ms.sendMessage("alert", { type: "danger", text: "Pdf meta data url is empty or null." })
                return
            }

            this.pdf = url
        })
    }
    
    /** 
     * 
    */
    protected phone = (digits) => {
        return formatPhone(digits)
    }
    
    /**
     * The user is done viewing the receipt.
     */
    protected done = (e: Event): void => {
        this.reset()
        this.closed.next(false)
    }

    /**
     * The user has chosen to start the refunding (fully or partially) process.
     * @param {Event} e 
     */
    protected initRefund = (e: Event): void => {
        this.form.get('refund').setValue(formatCurrency(this.receipt.amount - (this.receipt.refund || 0)))
        this.form.get('reason').setValue('')
        this.refunding = true
        this.voiding = false
        this.returning = false
    }

    /**
     * The user has chosen to refund (fully or partially) the payment.
     * @param e 
     */
    protected processRefund = (e: Event): void => {
        e.stopPropagation()
        e.preventDefault()

        const meta = {
            amount: clean(this.form.get('refund').value),
            reason: this.form.get('reason').value
        }

        this.master.patch(`receipts/${this.receipt.id}/refund`, meta, res => {
            this.refunding = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }
            
            this.reset()
            this.closed.next(true)
        })
    }

    /**
     * The user has chosen to start the void process.
     * @param {Event} e 
     */
    protected initVoid = (e: Event): void => {
        this.voidForm.get('voided').setValue(null)
        this.voidForm.get('reason').setValue('')
        this.refunding = false
        this.voiding = true
        this.returning = false
    }

    /**
     * The user has chosen to void the payment.
     * @param e 
     */
    protected processVoid = (e: Event): void => {
        e.stopPropagation()
        e.preventDefault()

        const meta = {
            voided: this.voidForm.get('voided').value,
            reason: this.voidForm.get('reason').value
        }

        this.master.patch(`receipts/${this.receipt.id}/void`, meta, res => {
            this.voiding = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }
            
            this.reset()
            this.closed.next(true)
        })
    }

    /**
     * The user has chosen to start the return process.
     * @param {Event} e 
     */
    protected initReturn = (e: Event): void => {
        this.returnForm.get('returned').setValue(null)
        this.returnForm.get('code').setValue('')
        this.returnForm.get('reason').setValue('')
        this.returnForm.get('fee').setValue('0.00')
        this.refunding = false
        this.voiding = false
        this.returning = true
    }

    /**
     * The user has chosen to return (bounce) the payment.
     * @param e 
     */
    protected processReturn = (e: Event): void => {
        e.stopPropagation()
        e.preventDefault()

        const meta = {
            returned: this.returnForm.get('returned').value,
            code: this.returnForm.get('code').value,
            nsf: (this.returnForm.get('code').value === 'R01'),
            reason: this.returnForm.get('reason').value,
            fee: clean(this.returnForm.get('fee').value)
        }

        this.master.patch(`receipts/${this.receipt.id}/return`, meta, res => {
            this.returning = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }
            
            this.reset()
            this.closed.next(true)
        })
    }
}
